import React, { useState, useRef } from 'react';
import axios from 'axios';
import './PDFUploader.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const PDFUploader = ({ onUploadSuccess }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [ocrSteps, setOcrSteps] = useState([]);
  const [currentStep, setCurrentStep] = useState(0);
  const [cancelling, setCancelling] = useState(false);
  const abortControllerRef = useRef(null);

  const OCR_STEPS = [
    { id: 1, label: 'Uploading file', icon: '📤' },
    { id: 2, label: 'Checking OCR service', icon: '🔍' },
    { id: 3, label: 'Starting OCR instance', icon: '🚀' },
    { id: 4, label: 'Waiting for OCR to be ready', icon: '⏳' },
    { id: 5, label: 'Processing PDF', icon: '⚙️' },
    { id: 6, label: 'Extracting text', icon: '📝' },
    { id: 7, label: 'Saving to database', icon: '💾' },
  ];

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      if (selectedFile.type !== 'application/pdf') {
        setError('Please select a PDF file');
        setFile(null);
        return;
      }
      setFile(selectedFile);
      setError(null);
      setResult(null);
      setOcrSteps([]);
      setCurrentStep(0);
      if (!isExpanded) {
        setIsExpanded(true);
      }
    }
  };

  const handleCancel = () => {
    setCancelling(true);
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
  };

  const updateStep = (stepId, status = 'active') => {
    setCurrentStep(stepId);
    setOcrSteps(prev => {
      const newSteps = [...prev];
      // Mark previous steps as complete
      for (let i = 0; i < stepId - 1; i++) {
        if (!newSteps[i] || newSteps[i].status !== 'complete') {
          newSteps[i] = { ...OCR_STEPS[i], status: 'complete' };
        }
      }
      // Set current step
      newSteps[stepId - 1] = { ...OCR_STEPS[stepId - 1], status };
      return newSteps;
    });
  };

  const handleUpload = async () => {
    if (!file) {
      setError('Please select a file first');
      return;
    }

    setUploading(true);
    setCancelling(false);
    setError(null);
    setResult(null);
    setOcrSteps([]);
    
    if (!isExpanded) {
      setIsExpanded(true);
    }

    abortControllerRef.current = new AbortController();

    try {
      // Step 1: Uploading file
      updateStep(1);
      
      const formData = new FormData();
      formData.append('file', file);

      // Simulate step progression based on time since OCR can take a while
      const stepInterval = setInterval(() => {
        setCurrentStep(prev => {
          if (prev < 6) {
            updateStep(prev + 1);
            return prev + 1;
          }
          return prev;
        });
      }, 15000); // Progress every 15 seconds

      // Step 2-3: Check/Start OCR (happens automatically on backend)
      updateStep(2);
      
      setTimeout(() => {
        if (uploading) updateStep(3);
      }, 2000);

      setTimeout(() => {
        if (uploading) updateStep(4);
      }, 5000);

      const response = await axios.post(
        `${API_BASE_URL}/insurance-documents/process-pdf`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          timeout: 1800000, // 30 minute timeout
          signal: abortControllerRef.current.signal,
          onUploadProgress: (progressEvent) => {
            if (progressEvent.total && progressEvent.loaded === progressEvent.total) {
              updateStep(2);
            }
          },
        }
      );

      clearInterval(stepInterval);

      // Mark all steps complete
      updateStep(7, 'complete');
      setOcrSteps(OCR_STEPS.map(step => ({ ...step, status: 'complete' })));

      setResult(response.data);
      setFile(null);
      
      const fileInput = document.getElementById('pdf-upload');
      if (fileInput) {
        fileInput.value = '';
      }
      
      if (onUploadSuccess) {
        onUploadSuccess();
      }

      // Auto-hide success message after 5 seconds
      setTimeout(() => {
        setResult(null);
        setOcrSteps([]);
        setCurrentStep(0);
      }, 5000);

    } catch (err) {
      console.error('Upload error:', err);
      
      if (axios.isCancel(err) || err.name === 'CanceledError' || err.code === 'ERR_CANCELED') {
        setError('Upload cancelled by user');
      } else {
        let errorMessage = 'Unknown error occurred';
        
        if (err.code === 'ECONNABORTED' || err.message?.includes('timeout')) {
          errorMessage = 'Request timed out. The OCR service may be taking too long. Please try again.';
        } else if (err.response?.data?.detail) {
          errorMessage = err.response.data.detail;
        } else if (err.response?.data?.message) {
          errorMessage = err.response.data.message;
        } else if (err.message) {
          errorMessage = err.message;
        }
        
        setError(`Upload failed: ${errorMessage}`);
      }
      
      setOcrSteps([]);
      setCurrentStep(0);
    } finally {
      setUploading(false);
      setCancelling(false);
      abortControllerRef.current = null;
    }
  };

  return (
    <div className="pdf-uploader">
      <div 
        className="upload-header"
        onClick={() => setIsExpanded(!isExpanded)}
        style={{ cursor: 'pointer' }}
      >
        <div className="upload-header-content">
          <div>
            <h2>Upload PDF</h2>
            <p className="upload-subtitle">Upload a PDF file to extract and process its content</p>
          </div>
          <div className={`chevron-icon ${isExpanded ? 'expanded' : ''}`}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M6 9L12 15L18 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
        </div>
      </div>

      <div className={`upload-container ${isExpanded ? 'expanded' : 'collapsed'}`}>
        <div className="file-input-wrapper">
          <input
            type="file"
            id="pdf-upload"
            accept=".pdf"
            onChange={handleFileChange}
            disabled={uploading}
            className="file-input"
          />
          <label htmlFor="pdf-upload" className="file-label">
            <div className="file-label-content">
              <div className="file-icon">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M14 2V8H20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M16 13H8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M16 17H8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M10 9H9H8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <div className="file-label-text">
                {file ? (
                  <>
                    <span className="file-name">{file.name}</span>
                    <span className="file-size">{(file.size / 1024 / 1024).toFixed(2)} MB</span>
                  </>
                ) : (
                  <>
                    <span className="file-label-main">Click to browse or drag and drop</span>
                    <span className="file-label-hint">PDF files only</span>
                  </>
                )}
              </div>
              {!file && (
                <div className="file-browse-button">
                  Browse Files
                </div>
              )}
            </div>
          </label>
        </div>

        {uploading ? (
          <button
            onClick={handleCancel}
            disabled={cancelling}
            className="upload-button cancel-upload-button"
          >
            {cancelling ? (
              <>
                <span className="button-spinner"></span>
                Cancelling...
              </>
            ) : (
              <>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                Cancel Upload
              </>
            )}
          </button>
        ) : (
          <button
            onClick={handleUpload}
            disabled={!file}
            className="upload-button"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M21 15V19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M7 10L12 15L17 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M12 15V3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Upload & Process
          </button>
        )}
      </div>

      {/* OCR Steps Progress */}
      {uploading && ocrSteps.length > 0 && (
        <div className="ocr-steps-container">
          <div className="ocr-steps-header">
            <span className="ocr-steps-title">Processing PDF with OCR</span>
            <span className="ocr-steps-subtitle">This may take a few minutes...</span>
          </div>
          <div className="ocr-steps-list">
            {OCR_STEPS.map((step, idx) => {
              const stepData = ocrSteps[idx];
              const status = stepData?.status || (idx < currentStep - 1 ? 'complete' : 'pending');
              const isActive = idx === currentStep - 1;
              
              return (
                <div key={step.id} className={`ocr-step ${status} ${isActive ? 'active' : ''}`}>
                  <span className="ocr-step-icon">
                    {status === 'complete' ? '✓' : step.icon}
                  </span>
                  <span className="ocr-step-label">{step.label}</span>
                  {isActive && <span className="ocr-step-spinner"></span>}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {error && (
        <div className="error-message">
          <span className="error-icon">❌</span>
          <span>{error}</span>
        </div>
      )}

      {result && (
        <div className="success-container">
          <div className="success-header">
            <span className="success-icon">✅</span>
            <h3>PDF Processed Successfully!</h3>
          </div>
          <div className="upload-results-list">
            <div className="upload-result-item success">
              <span className="result-icon">✓</span>
              <span className="result-filename">{result.file_name}</span>
              <span className="result-id">ID: {result.id}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PDFUploader;
